/**
 * Role Drawer Component (PR A8)
 * Create/Edit role interface
 */
import React, { useState, useEffect } from 'react';
import './RoleDrawer.css';

const RoleDrawer = ({ role, isOpen, onClose, onSave, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (role && mode === 'edit') {
      setFormData({
        name: role.name || '',
        description: role.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
    setErrors({});
  }, [role, mode, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    }

    if (formData.name.trim().length < 3) {
      newErrors.name = 'Role name must be at least 3 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isSystemRole = role?.is_system_role;
  const title = mode === 'create' ? 'Create Role' : 'Edit Role';

  return (
    <>
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} />

      {/* Drawer */}
      <div className="role-drawer">
        <div className="drawer-header">
          <h2>{title}</h2>
          <button className="drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="drawer-content">
          {isSystemRole && (
            <div className="system-role-warning">
              <strong>⚠️ System Role</strong>
              <p>System roles cannot be renamed or deleted.</p>
            </div>
          )}

          {/* Role Name */}
          <div className="form-group">
            <label className="form-label">
              Role Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="name"
              className={`form-input ${errors.name ? 'error' : ''}`}
              value={formData.name}
              onChange={handleChange}
              disabled={isSystemRole}
              placeholder="e.g., Manager, Auditor"
            />
            {errors.name && (
              <span className="error-message">{errors.name}</span>
            )}
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-textarea"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the role's purpose and responsibilities"
              rows={4}
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="error-banner">
              <strong>Error:</strong> {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="drawer-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Role'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default RoleDrawer;
